import { z } from 'zod';

import {
  materialCaptureTypeValues,
  materialCaptureUnitValues,
  materialCatalogSourceValues,
  materialOverrideScopeValues,
  materialPresentationTypeValues,
  type CreateGlobalMaterialRequest,
  type CreateGlobalMaterialResponse,
  type CreateLocalMaterialRequest,
  type CreateLocalMaterialResponse,
  type CreateMaterialOverrideRequest,
  type CreateMaterialOverrideResponse,
  type GetGlobalMaterialRequest,
  type GetGlobalMaterialResponse,
  type GlobalMaterialDto,
  type GlobalMaterialPresentationDto,
  type ListGlobalMaterialsRequest,
  type ListGlobalMaterialsResponse,
  type ListLocalMaterialsRequest,
  type ListLocalMaterialsResponse,
  type ListMaterialOverridesRequest,
  type ListMaterialOverridesResponse,
  type ListGlobalMaterialPriceVersionsRequest,
  type ListGlobalMaterialPriceVersionsResponse,
  type ListMaterialPriceOverrideVersionsRequest,
  type ListMaterialPriceOverrideVersionsResponse,
  type ListResolvedMaterialsRequest,
  type ListResolvedMaterialsResponse,
  type LocalMaterialDto,
  type MaterialCatalogSource,
  type MaterialCaptureType,
  type MaterialCaptureUnit,
  type MaterialOverrideDto,
  type GlobalMaterialPriceVersionRow,
  type MaterialPriceOverrideVersionRow,
  type MaterialOverrideScope,
  type MaterialPresentationType,
  type ResolvedMaterialDto,
  type ResolvedMaterialPresentationDto,
  type ScheduleMaterialPriceOverrideVersionRequest,
  type ScheduleMaterialPriceOverrideVersionResponse,
  type UpdateGlobalMaterialRequest,
  type UpdateGlobalMaterialResponse,
  type UpdateLocalMaterialRequest,
  type UpdateLocalMaterialResponse,
  type UpdateMaterialOverrideRequest,
  type UpdateMaterialOverrideResponse,
} from './materials-catalog.dto.js';

const dateSchema = z.iso.date();
const timestampSchema = z.iso.datetime();
const codeSchema = z.string().trim().min(1).max(50);
const displayNameSchema = z.string().trim().min(1);
const rateSchema = z.number().min(0);
const maxPiecesSchema = z.number().int().min(1);
const limitLbsSchema = z.number().min(0);
const orderingSchema = z.number().int().min(0);

export const MaterialPresentationTypeSchema: z.ZodType<MaterialPresentationType> = z.enum(
  materialPresentationTypeValues,
);
export const MaterialCaptureUnitSchema: z.ZodType<MaterialCaptureUnit> = z.enum(materialCaptureUnitValues);
export const MaterialCaptureTypeSchema: z.ZodType<MaterialCaptureType> = z.enum(materialCaptureTypeValues);
export const MaterialOverrideScopeSchema: z.ZodType<MaterialOverrideScope> = z.enum(materialOverrideScopeValues);
export const MaterialCatalogSourceSchema: z.ZodType<MaterialCatalogSource> = z.enum(materialCatalogSourceValues);
const MaterialPriceOverrideScopeSchema = z.enum(['company', 'store']);

const presentationBaseSchema = z.object({
  isCrvEligible: z.boolean(),
  isReportedToDoriis: z.boolean(),
  presentationOrder: orderingSchema.nullable().optional(),
});

const globalSwPresentationSchema = presentationBaseSchema.extend({
  type: z.literal('SW'),
  unit: z.literal('lb'),
  captureType: z.literal('weight'),
  allowsFractional: z.literal(true),
  baseRatePerLb: rateSchema,
});

const globalScPresentationSchema = presentationBaseSchema.extend({
  type: z.enum(['SCL', 'SCS', 'SC25']),
  unit: z.literal('piece'),
  captureType: z.literal('count'),
  allowsFractional: z.literal(false),
  depositPerPiece: rateSchema,
});

const globalSpPresentationSchema = presentationBaseSchema.extend({
  type: z.literal('SP'),
  unit: z.literal('lb'),
  captureType: z.literal('weight'),
  allowsFractional: z.literal(true),
  ratePerLb: rateSchema,
});

export const GlobalMaterialPresentationDtoSchema: z.ZodType<GlobalMaterialPresentationDto> = z.discriminatedUnion(
  'type',
  [globalSwPresentationSchema, globalScPresentationSchema, globalSpPresentationSchema],
);

const resolvedSwPresentationSchema = presentationBaseSchema.extend({
  type: z.literal('SW'),
  unit: z.literal('lb'),
  captureType: z.literal('weight'),
  allowsFractional: z.literal(true),
  baseRatePerLb: rateSchema,
  premiumPerLb: rateSchema,
  effectiveRatePerLb: rateSchema,
});

const resolvedScPresentationSchema = presentationBaseSchema.extend({
  type: z.enum(['SCL', 'SCS', 'SC25']),
  unit: z.literal('piece'),
  captureType: z.literal('count'),
  allowsFractional: z.literal(false),
  depositPerPiece: rateSchema,
});

const resolvedSpPresentationSchema = presentationBaseSchema.extend({
  type: z.literal('SP'),
  unit: z.literal('lb'),
  captureType: z.literal('weight'),
  allowsFractional: z.literal(true),
  ratePerLb: rateSchema,
});

export const ResolvedMaterialPresentationDtoSchema: z.ZodType<ResolvedMaterialPresentationDto> = z.discriminatedUnion(
  'type',
  [resolvedSwPresentationSchema, resolvedScPresentationSchema, resolvedSpPresentationSchema],
);

export const GlobalMaterialDtoSchema: z.ZodType<GlobalMaterialDto> = z.object({
  id: z.uuid(),
  code: codeSchema.nullable(),
  displayName: displayNameSchema,
  categoryOrder: orderingSchema.nullable().optional(),
  materialOrder: orderingSchema.nullable().optional(),
  isActive: z.boolean(),
  isCrvEligible: z.boolean(),
  maxPiecesPerLine: maxPiecesSchema,
  dailyCrvLimitLbs: limitLbsSchema.nullable(),
  presentations: z.array(GlobalMaterialPresentationDtoSchema).nonempty(),
  effectiveFrom: dateSchema,
  effectiveTo: dateSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const MaterialOverrideDtoSchema: z.ZodType<MaterialOverrideDto> = z
  .object({
    id: z.uuid(),
    companyId: z.uuid(),
    scope: MaterialOverrideScopeSchema,
    storeId: z.uuid().nullable(),
    locationId: z.uuid().nullable(),
    materialId: z.uuid(),
    displayName: displayNameSchema.nullable(),
    isActive: z.boolean().nullable(),
    localCode: codeSchema.nullable(),
    swPremiumPerLb: rateSchema.nullable(),
    spRatePerLb: rateSchema.nullable(),
    maxPiecesPerLine: maxPiecesSchema.nullable(),
    dailyCrvLimitLbs: limitLbsSchema.nullable(),
    effectiveFrom: dateSchema,
    effectiveTo: dateSchema.nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .superRefine((value, ctx) => {
    if (value.scope === 'company') {
      if (value.storeId || value.locationId) {
        ctx.addIssue({
          code: 'custom',
          path: ['storeId'],
          message: 'Company overrides cannot include store or location scope.',
        });
      }
    }
    if (value.scope === 'store') {
      if (!value.storeId || value.locationId) {
        ctx.addIssue({
          code: 'custom',
          path: ['storeId'],
          message: 'Store overrides require storeId and no locationId.',
        });
      }
    }
    if (value.scope === 'location') {
      if (!value.storeId || !value.locationId) {
        ctx.addIssue({
          code: 'custom',
          path: ['locationId'],
          message: 'Location overrides require storeId and locationId.',
        });
      }
    }
  });

export const GlobalMaterialPriceVersionRowSchema: z.ZodType<GlobalMaterialPriceVersionRow> = z
  .object({
    id: z.uuid(),
    materialId: z.uuid(),
    presentationType: MaterialPresentationTypeSchema,
    baseRatePerLb: rateSchema.nullable(),
    depositPerPiece: rateSchema.nullable(),
    ratePerLb: rateSchema.nullable(),
    effectiveAtUtc: timestampSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .superRefine((value, ctx) => {
    if (value.presentationType === 'SW') {
      if (value.baseRatePerLb === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['baseRatePerLb'],
          message: 'SW price versions require baseRatePerLb.',
        });
      }
      if (value.depositPerPiece !== null || value.ratePerLb !== null) {
        ctx.addIssue({
          code: 'custom',
          path: ['presentationType'],
          message: 'SW price versions cannot include depositPerPiece or ratePerLb.',
        });
      }
      return;
    }

    if (value.presentationType === 'SP') {
      if (value.ratePerLb === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['ratePerLb'],
          message: 'SP price versions require ratePerLb.',
        });
      }
      if (value.baseRatePerLb !== null || value.depositPerPiece !== null) {
        ctx.addIssue({
          code: 'custom',
          path: ['presentationType'],
          message: 'SP price versions cannot include baseRatePerLb or depositPerPiece.',
        });
      }
      return;
    }

    if (value.depositPerPiece === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['depositPerPiece'],
        message: 'SC price versions require depositPerPiece.',
      });
    }
    if (value.baseRatePerLb !== null || value.ratePerLb !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['presentationType'],
        message: 'SC price versions cannot include baseRatePerLb or ratePerLb.',
      });
    }
  });

export const MaterialPriceOverrideVersionRowSchema: z.ZodType<MaterialPriceOverrideVersionRow> = z
  .object({
    id: z.uuid(),
    companyId: z.uuid(),
    scope: MaterialPriceOverrideScopeSchema,
    storeId: z.uuid().nullable(),
    materialId: z.uuid(),
    swPremiumPerLb: rateSchema.nullable(),
    spRatePerLb: rateSchema.nullable(),
    effectiveAtUtc: timestampSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .superRefine((value, ctx) => {
    if (value.scope === 'store' && value.storeId === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['storeId'],
        message: 'Store price overrides require storeId.',
      });
    }
    if (value.scope === 'company' && value.storeId !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['storeId'],
        message: 'Company price overrides cannot include storeId.',
      });
    }
  });

export const ResolvedMaterialDtoSchema: z.ZodType<ResolvedMaterialDto> = z
  .object({
    id: z.uuid(),
    source: MaterialCatalogSourceSchema,
    displayName: displayNameSchema,
    code: codeSchema.nullable(),
    categoryOrder: orderingSchema.nullable().optional(),
    materialOrder: orderingSchema.nullable().optional(),
    isActive: z.boolean(),
    isCrvEligible: z.boolean(),
    maxPiecesPerLine: maxPiecesSchema,
    dailyCrvLimitLbs: limitLbsSchema.nullable(),
    presentations: z.array(ResolvedMaterialPresentationDtoSchema).nonempty(),
    overrideScope: MaterialOverrideScopeSchema.nullable(),
  })
  .superRefine((value, ctx) => {
    if (value.source === 'local' && value.overrideScope !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['overrideScope'],
        message: 'Local materials cannot declare override scope.',
      });
    }
  });

export const LocalMaterialDtoSchema: z.ZodType<LocalMaterialDto> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid(),
  stationId: z.uuid(),
  source: z.literal('local'),
  code: codeSchema.nullable(),
  displayName: displayNameSchema,
  isActive: z.boolean(),
  isCrvEligible: z.boolean(),
  maxPiecesPerLine: maxPiecesSchema,
  dailyCrvLimitLbs: limitLbsSchema.nullable(),
  presentations: z.array(GlobalMaterialPresentationDtoSchema).nonempty(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const ListGlobalMaterialsRequestSchema: z.ZodType<ListGlobalMaterialsRequest> = z.object({
  companyId: z.uuid(),
  effectiveOn: dateSchema.optional(),
  includeInactive: z.boolean().optional(),
});

export const ListGlobalMaterialsResponseSchema: z.ZodType<ListGlobalMaterialsResponse> = z.object({
  items: z.array(GlobalMaterialDtoSchema),
});

export const GetGlobalMaterialRequestSchema: z.ZodType<GetGlobalMaterialRequest> = z.object({
  companyId: z.uuid(),
  materialId: z.uuid(),
});

export const GetGlobalMaterialResponseSchema: z.ZodType<GetGlobalMaterialResponse> = z.object({
  material: GlobalMaterialDtoSchema,
});

export const CreateGlobalMaterialRequestSchema: z.ZodType<CreateGlobalMaterialRequest> = z.object({
  displayName: displayNameSchema,
  code: codeSchema.nullable().optional(),
  isActive: z.boolean(),
  isCrvEligible: z.boolean(),
  maxPiecesPerLine: maxPiecesSchema,
  dailyCrvLimitLbs: limitLbsSchema.nullable().optional(),
  presentations: z.array(GlobalMaterialPresentationDtoSchema).nonempty(),
  effectiveFrom: dateSchema,
  effectiveTo: dateSchema.nullable().optional(),
});

export const CreateGlobalMaterialResponseSchema: z.ZodType<CreateGlobalMaterialResponse> = z.object({
  material: GlobalMaterialDtoSchema,
});

export const UpdateGlobalMaterialRequestSchema: z.ZodType<UpdateGlobalMaterialRequest> = z.object({
  materialId: z.uuid(),
  displayName: displayNameSchema.optional(),
  code: codeSchema.nullable().optional(),
  isActive: z.boolean().optional(),
  isCrvEligible: z.boolean().optional(),
  maxPiecesPerLine: maxPiecesSchema.optional(),
  dailyCrvLimitLbs: limitLbsSchema.nullable().optional(),
  presentations: z.array(GlobalMaterialPresentationDtoSchema).nonempty().optional(),
  effectiveFrom: dateSchema.optional(),
  effectiveTo: dateSchema.nullable().optional(),
});

export const UpdateGlobalMaterialResponseSchema: z.ZodType<UpdateGlobalMaterialResponse> = z.object({
  material: GlobalMaterialDtoSchema,
});

export const ListMaterialOverridesRequestSchema: z.ZodType<ListMaterialOverridesRequest> = z
  .object({
    companyId: z.uuid(),
    materialId: z.uuid().optional(),
    storeId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    effectiveOn: dateSchema.optional(),
  })
  .superRefine((value, ctx) => {
    if (value.locationId && !value.storeId) {
      ctx.addIssue({
        code: 'custom',
        path: ['storeId'],
        message: 'storeId is required when locationId is provided.',
      });
    }
  });

export const ListMaterialOverridesResponseSchema: z.ZodType<ListMaterialOverridesResponse> = z.object({
  items: z.array(MaterialOverrideDtoSchema),
});

export const ListGlobalMaterialPriceVersionsRequestSchema: z.ZodType<ListGlobalMaterialPriceVersionsRequest> =
  z.object({
    companyId: z.uuid(),
    materialId: z.uuid().optional(),
    presentationType: MaterialPresentationTypeSchema.optional(),
  });

export const ListGlobalMaterialPriceVersionsResponseSchema: z.ZodType<ListGlobalMaterialPriceVersionsResponse> =
  z.object({
    items: z.array(GlobalMaterialPriceVersionRowSchema),
  });

export const ListMaterialPriceOverrideVersionsRequestSchema: z.ZodType<ListMaterialPriceOverrideVersionsRequest> =
  z.object({
    companyId: z.uuid(),
    materialId: z.uuid().optional(),
    scope: MaterialPriceOverrideScopeSchema.optional(),
    storeId: z.uuid().optional(),
  });

export const ListMaterialPriceOverrideVersionsResponseSchema: z.ZodType<ListMaterialPriceOverrideVersionsResponse> =
  z.object({
    items: z.array(MaterialPriceOverrideVersionRowSchema),
  });

export const ScheduleMaterialPriceOverrideVersionRequestSchema: z.ZodType<ScheduleMaterialPriceOverrideVersionRequest> =
  z
    .object({
      companyId: z.uuid(),
      materialId: z.uuid(),
      scope: MaterialPriceOverrideScopeSchema,
      storeId: z.uuid().nullable().optional(),
      effectiveOn: dateSchema,
      effectiveStoreId: z.uuid(),
      swPremiumPerLb: rateSchema.nullable().optional(),
      spRatePerLb: rateSchema.nullable().optional(),
    })
    .superRefine((value, ctx) => {
      const normalizedStoreId = value.storeId ?? null;
      if (value.scope === 'store' && normalizedStoreId === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['storeId'],
          message: 'Store price overrides require storeId.',
        });
      }
      if (value.scope === 'company' && normalizedStoreId !== null) {
        ctx.addIssue({
          code: 'custom',
          path: ['storeId'],
          message: 'Company price overrides cannot include storeId.',
        });
      }
    });

export const ScheduleMaterialPriceOverrideVersionResponseSchema: z.ZodType<ScheduleMaterialPriceOverrideVersionResponse> =
  z.object({
    version: MaterialPriceOverrideVersionRowSchema,
  });

export const CreateMaterialOverrideRequestSchema: z.ZodType<CreateMaterialOverrideRequest> = z
  .object({
    companyId: z.uuid(),
    materialId: z.uuid(),
    scope: MaterialOverrideScopeSchema,
    storeId: z.uuid().nullable().optional(),
    locationId: z.uuid().nullable().optional(),
    displayName: displayNameSchema.nullable().optional(),
    isActive: z.boolean().nullable().optional(),
    localCode: codeSchema.nullable().optional(),
    swPremiumPerLb: rateSchema.nullable().optional(),
    spRatePerLb: rateSchema.nullable().optional(),
    maxPiecesPerLine: maxPiecesSchema.nullable().optional(),
    dailyCrvLimitLbs: limitLbsSchema.nullable().optional(),
    effectiveFrom: dateSchema,
    effectiveTo: dateSchema.nullable().optional(),
  })
  .superRefine((value, ctx) => {
    const storeId = value.storeId ?? null;
    const locationId = value.locationId ?? null;
    if (value.scope === 'company') {
      if (storeId || locationId) {
        ctx.addIssue({
          code: 'custom',
          path: ['storeId'],
          message: 'Company overrides cannot include store or location scope.',
        });
      }
    }
    if (value.scope === 'store') {
      if (!storeId || locationId) {
        ctx.addIssue({
          code: 'custom',
          path: ['storeId'],
          message: 'Store overrides require storeId and no locationId.',
        });
      }
    }
    if (value.scope === 'location') {
      if (!storeId || !locationId) {
        ctx.addIssue({
          code: 'custom',
          path: ['locationId'],
          message: 'Location overrides require storeId and locationId.',
        });
      }
    }
  });

export const CreateMaterialOverrideResponseSchema: z.ZodType<CreateMaterialOverrideResponse> = z.object({
  override: MaterialOverrideDtoSchema,
});

export const UpdateMaterialOverrideRequestSchema: z.ZodType<UpdateMaterialOverrideRequest> = z.object({
  companyId: z.uuid(),
  overrideId: z.uuid(),
  displayName: displayNameSchema.nullable().optional(),
  isActive: z.boolean().nullable().optional(),
  localCode: codeSchema.nullable().optional(),
  swPremiumPerLb: rateSchema.nullable().optional(),
  spRatePerLb: rateSchema.nullable().optional(),
  maxPiecesPerLine: maxPiecesSchema.nullable().optional(),
  dailyCrvLimitLbs: limitLbsSchema.nullable().optional(),
  effectiveFrom: dateSchema.optional(),
  effectiveTo: dateSchema.nullable().optional(),
});

export const UpdateMaterialOverrideResponseSchema: z.ZodType<UpdateMaterialOverrideResponse> = z.object({
  override: MaterialOverrideDtoSchema,
});

export const ListResolvedMaterialsRequestSchema: z.ZodType<ListResolvedMaterialsRequest> = z
  .object({
    companyId: z.uuid(),
    storeId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    stationId: z.uuid().optional(),
    effectiveOn: dateSchema.optional(),
    includeInactive: z.boolean().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.locationId && !value.storeId) {
      ctx.addIssue({
        code: 'custom',
        path: ['storeId'],
        message: 'storeId is required when locationId is provided.',
      });
    }
    if (value.stationId) {
      if (!value.locationId || !value.storeId) {
        ctx.addIssue({
          code: 'custom',
          path: ['stationId'],
          message: 'stationId requires storeId and locationId.',
        });
      }
    }
  });

export const ListResolvedMaterialsResponseSchema: z.ZodType<ListResolvedMaterialsResponse> = z.object({
  items: z.array(ResolvedMaterialDtoSchema),
});

export const ListLocalMaterialsRequestSchema: z.ZodType<ListLocalMaterialsRequest> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid(),
  stationId: z.uuid(),
  includeInactive: z.boolean().optional(),
});

export const ListLocalMaterialsResponseSchema: z.ZodType<ListLocalMaterialsResponse> = z.object({
  items: z.array(LocalMaterialDtoSchema),
});

export const CreateLocalMaterialRequestSchema: z.ZodType<CreateLocalMaterialRequest> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid(),
  stationId: z.uuid(),
  displayName: displayNameSchema,
  code: codeSchema.nullable().optional(),
  isActive: z.boolean(),
  isCrvEligible: z.boolean(),
  maxPiecesPerLine: maxPiecesSchema,
  dailyCrvLimitLbs: limitLbsSchema.nullable().optional(),
  presentations: z.array(GlobalMaterialPresentationDtoSchema).nonempty(),
});

export const CreateLocalMaterialResponseSchema: z.ZodType<CreateLocalMaterialResponse> = z.object({
  material: LocalMaterialDtoSchema,
});

export const UpdateLocalMaterialRequestSchema: z.ZodType<UpdateLocalMaterialRequest> = z.object({
  companyId: z.uuid(),
  localMaterialId: z.uuid(),
  displayName: displayNameSchema.optional(),
  code: codeSchema.nullable().optional(),
  isActive: z.boolean().optional(),
  isCrvEligible: z.boolean().optional(),
  maxPiecesPerLine: maxPiecesSchema.optional(),
  dailyCrvLimitLbs: limitLbsSchema.nullable().optional(),
  presentations: z.array(GlobalMaterialPresentationDtoSchema).nonempty().optional(),
});

export const UpdateLocalMaterialResponseSchema: z.ZodType<UpdateLocalMaterialResponse> = z.object({
  material: LocalMaterialDtoSchema,
});
